import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  Download,
  FileUp,
  Menu,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Settings2,
  Sparkles,
  X,
} from 'lucide-react';
import type { AppPage } from '../navigation';
import { moduleConfigs } from '../operations/moduleConfigs';
import { moduleDataSource } from '../operations/moduleDataSource';
import type { ModuleAction, ModuleRow } from '../operations/types';

const actionIcons = {
  export: Download,
  sync: RefreshCw,
  reset: RotateCcw,
  add: Plus,
  advance: Sparkles,
  import: FileUp,
  rules: Settings2,
} as const;

function asText(value: unknown) {
  return value === undefined || value === null ? '' : String(value);
}

function exportCsv(title: string, columns: { key: string; label: string }[], rows: ModuleRow[]) {
  const csv = '\ufeff' + [columns.map((column) => column.label), ...rows.map((row) => columns.map((column) => asText(row[column.key])))]
    .map((line) => line.map((cell) => `"${cell.replaceAll('"', '""')}"`).join(','))
    .join('\r\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = Object.assign(document.createElement('a'), { href: url, download: `${title}.csv` });
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function nextStatus(current: string, flow: string[]) {
  const index = flow.indexOf(current);
  return flow[Math.min(Math.max(index + 1, 0), flow.length - 1)] ?? current;
}

export function OperationsModulePage({ page, onMenuOpen }: { page: AppPage; onMenuOpen: () => void }) {
  const config = moduleConfigs[page];
  const [rows, setRows] = useState<ModuleRow[]>([]);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<ModuleRow | null>(null);
  const [formMode, setFormMode] = useState<'add' | 'rules' | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const importRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!config) return;
    let active = true;
    moduleDataSource.load(config).then((loaded) => active && setRows(loaded));
    return () => { active = false; };
  }, [config]);

  useEffect(() => {
    if (!config || !rows.length) return;
    moduleDataSource.save(config, rows);
  }, [config, rows]);

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (query && !Object.values(row).some((value) => asText(value).toLowerCase().includes(query))) return false;
      return Object.entries(filters).every(([key, value]) => !value || asText(row[key]) === value);
    });
  }, [filters, rows, search]);

  if (!config) return null;

  const updateRows = (next: ModuleRow[], message: string) => {
    setRows(next);
    setNotice(message);
    window.setTimeout(() => setNotice(null), 2400);
  };

  const runAction = async (action: ModuleAction) => {
    if (action.kind === 'export') {
      exportCsv(config.title, config.columns, visibleRows);
      setNotice(`已导出 ${visibleRows.length} 条数据`);
      return;
    }
    if (action.kind === 'add') {
      setFormData({});
      setFormMode('add');
      return;
    }
    if (action.kind === 'import') {
      importRef.current?.click();
      return;
    }
    setBusyAction(action.kind);
    if (action.kind === 'reset') {
      updateRows(config.seed.map((row) => ({ ...row })), '演示数据已恢复');
    } else if (action.kind === 'sync') {
      const synced = await moduleDataSource.sync(config, rows);
      updateRows(synced, synced.length > rows.length ? `同步完成，新增 ${synced.length - rows.length} 条` : '同步完成，没有重复写入数据');
    } else if (action.kind === 'advance' && config.statusField && config.statusFlow) {
      const targets = selectedIds.size ? selectedIds : new Set(rows.map((row) => row.id));
      let next = rows;
      if (page === 'invoice' && action.label.includes('重试')) {
        next = rows.map((row) => row.status === '资料异常' ? { ...row, status: '待开票' } : row);
      } else if (page === 'invoice') {
        next = rows.map((row) => targets.has(row.id) && row.status !== '资料异常' ? { ...row, status: '已完成', upload: '已上传' } : row);
      } else if (page === 'automation' && action.label.includes('分派')) {
        next = rows.map((row) => row.status === '待分派' ? { ...row, status: '处理中', agent: row.type === '物流签收' ? '物流客服' : '售后组' } : row);
      } else if (page === 'automation') {
        next = rows.map((row) => targets.has(row.id) && row.status !== '已超时' ? { ...row, status: '已完成' } : row);
      } else if (page === 'product') {
        next = rows.map((row) => ({ ...row, status: '已完善', scripts: '售前售后话术完整' }));
      } else {
        const finalStatus = config.statusFlow.at(-1) ?? '已完成';
        next = rows.map((row) => targets.has(row.id) ? { ...row, [config.statusField!]: finalStatus } : row);
      }
      setSelectedIds(new Set());
      updateRows(next, '批量处理已完成');
    } else if (action.kind === 'rules') {
      try {
        setFormData(JSON.parse(localStorage.getItem(`${config.storageKey}-settings`) ?? '{}') as Record<string, string>);
      } catch {
        setFormData({});
      }
      setFormMode('rules');
    }
    window.setTimeout(() => setBusyAction(null), 450);
  };

  const submitForm = () => {
    if (!config.formFields) return;
    if (config.formFields.some((field) => field.required && !formData[field.key]?.trim())) {
      setNotice('请填写完整的必填信息');
      return;
    }
    if (formMode === 'rules') {
      localStorage.setItem(`${config.storageKey}-settings`, JSON.stringify(formData));
      setNotice('配置已保存，后续同步将使用当前规则');
      setFormMode(null);
      return;
    }
    const generatedId = `${page.toUpperCase()}-${Date.now().toString().slice(-6)}`;
    const row: ModuleRow = { id: generatedId };
    config.formFields.forEach((field) => { row[field.key] = field.type === 'number' ? Number(formData[field.key]) : formData[field.key] ?? ''; });
    if (config.statusField) row[config.statusField] = config.statusFlow?.[0] ?? '待处理';
    if (page === 'product') {
      row.scripts = '等待生成话术';
      row.status = '资料缺失';
    }
    if (page === 'bills') {
      row.bill = generatedId;
      row.invoices = `${Number(row.invoices)}张`;
      row.amount = `¥${Number(row.amountValue).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`;
      row.status = asText(row.screenshots).trim() ? '资料完整' : '待补截图';
    }
    updateRows([row, ...rows], '新记录已保存');
    setFormMode(null);
  };

  const advanceRow = (row: ModuleRow) => {
    if (!config.statusField || !config.statusFlow) return;
    const status = asText(row[config.statusField]);
    const next = nextStatus(status, config.statusFlow);
    const updated = rows.map((item) => item.id === row.id ? { ...item, [config.statusField!]: next } : item);
    updateRows(updated, `${row.id} 已更新为“${next}”`);
    setSelected({ ...row, [config.statusField]: next });
  };

  const importCsv = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    const lines = text.replace(/^\ufeff/, '').split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) { setNotice('CSV 中没有可导入的数据'); return; }
    const headers = lines[0]!.split(',').map((value) => value.replaceAll('"', '').trim());
    const imported = lines.slice(1).map((line, index) => {
      const values = line.split(',').map((value) => value.replace(/^"|"$/g, '').trim());
      const row: ModuleRow = { id: `IMPORT-${Date.now()}-${index}` };
      config.columns.forEach((column) => {
        const headerIndex = headers.indexOf(column.label);
        if (headerIndex >= 0) row[column.key] = values[headerIndex] ?? '';
      });
      return row;
    });
    updateRows([...imported, ...rows], `已导入 ${imported.length} 条数据`);
  };

  return (
    <main className="main operationsMain">
      <header className="topbar operationsTopbar">
        <button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button>
        <strong>{config.title}</strong><span>运营工作台</span>
      </header>
      <div className="operationsContent">
        <section className="operationsHead">
          <div><h1>{config.title}</h1><p>{config.subtitle}</p></div>
          <div className="operationsActions">
            {config.actions.map((action) => {
              const Icon = actionIcons[action.kind];
              return <button key={`${action.kind}-${action.label}`} className={`button ${action.primary ? 'primary' : ''}`} disabled={busyAction === action.kind} onClick={() => runAction(action)}><Icon className={busyAction === action.kind ? 'spin' : ''} />{action.label}</button>;
            })}
            <input ref={importRef} type="file" accept=".csv,text/csv" hidden onChange={(event) => importCsv(event.target.files?.[0])} />
          </div>
        </section>

        {config.notice && <section className={`operationsNotice ${config.notice.tone ?? 'teal'}`}><Settings2 /><div><strong>{config.notice.title}</strong><span>{config.notice.detail}</span></div></section>}

        <section className="operationsKpis">
          {config.metrics.map((metric) => <article key={metric.label}><div><span>{metric.label}</span><i className={metric.tone} /></div><strong>{metric.value(rows)}</strong><small>{metric.note}</small></article>)}
        </section>

        <section className="operationsPanel">
          <header>
            <h2>{config.title}明细</h2>
            <div className="operationsFilters">
              <label><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={config.searchPlaceholder} /></label>
              {config.filters.map((filter) => {
                const options = [...new Set(rows.map((row) => asText(row[filter.key])).filter(Boolean))];
                return <select key={filter.key} value={filters[filter.key] ?? ''} onChange={(event) => setFilters((current) => ({ ...current, [filter.key]: event.target.value }))}><option value="">{filter.label}</option>{options.map((option) => <option key={option}>{option}</option>)}</select>;
              })}
            </div>
          </header>
          <div className="operationsTableScroll">
            <table className="operationsTable">
              <thead><tr>{page === 'invoice' && <th><input type="checkbox" aria-label="全选" checked={visibleRows.length > 0 && visibleRows.every((row) => selectedIds.has(row.id))} onChange={(event) => setSelectedIds(event.target.checked ? new Set(visibleRows.map((row) => row.id)) : new Set())} /></th>}{config.columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr></thead>
              <tbody>{visibleRows.map((row) => <tr key={row.id} onClick={() => setSelected(row)}>{page === 'invoice' && <td onClick={(event) => event.stopPropagation()}><input type="checkbox" aria-label={`选择${row.id}`} checked={selectedIds.has(row.id)} onChange={(event) => setSelectedIds((current) => { const next = new Set(current); if (event.target.checked) next.add(row.id); else next.delete(row.id); return next; })} /></td>}{config.columns.map((column) => <td key={column.key} className={column.kind ?? 'text'}>{column.kind === 'status' && <i className={`rowDot ${asText(row[column.key]).includes('风险') || asText(row[column.key]).includes('异常') || asText(row[column.key]).includes('待') ? 'warning' : 'success'}`} />}{asText(row[column.key])}</td>)}</tr>)}</tbody>
            </table>
            {!visibleRows.length && <div className="operationsEmpty">当前条件下没有数据</div>}
          </div>
          <footer><span>共 {visibleRows.length} 条</span><span>点击任意记录查看详情和处理</span></footer>
        </section>
      </div>

      {selected && <div className="modalLayer" onMouseDown={(event) => event.target === event.currentTarget && setSelected(null)}><section className="operationsModal">
        <header><div><strong>{config.title}详情</strong><span>{selected.id}</span></div><button className="iconButton" aria-label="关闭" onClick={() => setSelected(null)}><X /></button></header>
        <div className="operationsDetailGrid">{config.columns.map((column) => <div key={column.key}><span>{column.label}</span><strong>{asText(selected[column.key]) || '--'}</strong></div>)}</div>
        <footer><button className="button" onClick={() => setSelected(null)}>关闭</button>{config.statusField && config.statusFlow && <button className="button primary" onClick={() => advanceRow(selected)}><CheckCircle2 />{config.rowActionLabel ?? '更新处理状态'}</button>}</footer>
      </section></div>}

      {formMode && <div className="modalLayer" onMouseDown={(event) => event.target === event.currentTarget && setFormMode(null)}><section className="operationsModal">
        <header><div><strong>{formMode === 'rules' ? `${config.title}配置` : `新增${config.title}记录`}</strong><span>{formMode === 'rules' ? '配置会保存到当前工作台' : '保存后立即进入当前列表'}</span></div><button className="iconButton" aria-label="关闭" onClick={() => setFormMode(null)}><X /></button></header>
        <div className="operationsForm">{config.formFields?.map((field) => <label key={field.key}><span>{field.label}{field.required ? ' *' : ''}</span>{field.type === 'select' ? <select value={formData[field.key] ?? ''} onChange={(event) => setFormData((current) => ({ ...current, [field.key]: event.target.value }))}><option value="">请选择</option>{field.options?.map((option) => <option key={option}>{option}</option>)}</select> : field.type === 'textarea' ? <textarea value={formData[field.key] ?? ''} onChange={(event) => setFormData((current) => ({ ...current, [field.key]: event.target.value }))} /> : <input type={field.type === 'number' ? 'number' : field.type === 'month' ? 'month' : 'text'} value={formData[field.key] ?? ''} onChange={(event) => setFormData((current) => ({ ...current, [field.key]: event.target.value }))} />}</label>) ?? <p>当前规则使用系统默认配置。</p>}</div>
        <footer><button className="button" onClick={() => setFormMode(null)}>取消</button>{config.formFields && <button className="button primary" onClick={submitForm}>保存</button>}</footer>
      </section></div>}
      {notice && <button className="toast" onClick={() => setNotice(null)}>{notice}</button>}
    </main>
  );
}
